import { env } from '@/lib/env';
import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

interface RequestOptions extends Omit<AxiosRequestConfig, 'url' | 'method' | 'data'> {
  body?: unknown;
}

const AUTH_TOKEN_STORAGE_KEY = 'localfirst_admin_auth_token';
const AUTH_FLAG_STORAGE_KEY = 'localfirst_admin_auth';

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export const getAuthToken = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
};

export const setAuthToken = (token: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
};

export const clearAuthToken = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
};

const axiosInstance = axios.create({
  baseURL: env.apiBaseUrl || undefined,
  headers: {
    'Content-Type': 'application/json',
  },
});

let lastAuthToastAt = 0;
const AUTH_TOAST_DEDUP_MS = 4000;
let lastAuthEventAt = 0;
const AUTH_EVENT_DEDUP_MS = 4000;

axiosInstance.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<any>) => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const now = Date.now();
      if (now - lastAuthToastAt > AUTH_TOAST_DEDUP_MS) {
        lastAuthToastAt = now;
        const apiMessage = error.response?.data?.message;
        toast.error(
          typeof apiMessage === 'string' && apiMessage.trim()
            ? apiMessage
            : 'Session expired. Please log in again.'
        );
      }
      if (now - lastAuthEventAt > AUTH_EVENT_DEDUP_MS) {
        lastAuthEventAt = now;
        window.dispatchEvent(new CustomEvent('auth:expired'));
      }
      clearAuthToken();
      window.localStorage.removeItem(AUTH_FLAG_STORAGE_KEY);
    }
    return Promise.reject(error);
  }
);

const getErrorMessage = (error: AxiosError<any>) => {
  const responseMessage = error.response?.data?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage;
  }
  if (typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }
  return `Request failed with status ${error.response?.status ?? 500}`;
};

async function request<T>(
  method: AxiosRequestConfig['method'],
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, ...config } = options;

  try {
    const response = await axiosInstance.request<T>({
      ...config,
      url: path,
      method,
      data: body,
    });

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<any>;
    throw new ApiError(getErrorMessage(axiosError), axiosError.response?.status ?? 500);
  }
}

export const apiClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, options),
};

export { ApiError };
